import { useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPhone(value) {
  const digits = value.replace(/\D/g, "");
  let d = digits;
  if (d.startsWith("8") && d.length <= 11) d = "7" + d.slice(1);
  if (d.startsWith("9") && d.length <= 10) d = "7" + d;
  if (!d.startsWith("7") && d.length > 0) d = "7" + d;
  let out = "";
  if (d.length > 0) out += "+7";
  if (d.length > 1) out += " (" + d.slice(1, 4);
  if (d.length > 4) out += ") " + d.slice(4, 7);
  if (d.length > 7) out += "-" + d.slice(7, 9);
  if (d.length > 9) out += "-" + d.slice(9, 11);
  return out;
}

function validateName(v) {
  if (!v || v.trim().length < 2) return "Имя слишком короткое";
  if (!/^[А-ЯЁа-яё][А-ЯЁа-яё\s\-]{1,49}$/.test(v.trim())) return "Введите имя на русском языке";
  return "";
}

function validatePhone(v) {
  const digits = v.replace(/\D/g, "");
  if (digits.length < 11) return "Введите полный номер телефона";
  if (new Set(digits.slice(1)).size === 1) return "Некорректный номер";
  return "";
}

export default function FeedbackButton({ settings }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", messenger: "Telegram", message: "" });
  const [touched, setTouched] = useState({});

  const nameErr = touched.name ? validateName(form.name) : "";
  const phoneErr = touched.phone ? validatePhone(form.phone) : "";
  const canSubmit = !validateName(form.name) && !validatePhone(form.phone);

  const handlePhoneChange = (val) => {
    setForm(p => ({ ...p, phone: formatPhone(val) }));
    if (!touched.phone) setTouched(p => ({ ...p, phone: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, phone: true });
    if (!canSubmit) return;
    setLoading(true);
    try {
      const digits = form.phone.replace(/\D/g, "");
      await axios.post(`${API}/feedback`, { ...form, phone: "+" + digits });
      setSuccess(true);
      setTimeout(() => { setOpen(false); setSuccess(false); setForm({ name: "", phone: "", messenger: "Telegram", message: "" }); setTouched({}); }, 3000);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (typeof msg === "string") toast.error(msg);
      else if (Array.isArray(msg)) toast.error(msg[0]?.msg || "Ошибка");
      else toast.error("Ошибка при отправке");
    } finally { setLoading(false); }
  };

  const tg = settings?.contacts_telegram || "";
  const wa = settings?.contacts_whatsapp || "";

  const inputCls = (err) =>
    `w-full bg-[#0A0A0A] border rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#555] outline-none transition-colors ${
      err ? "border-red-500/60" : "border-white/10 focus:border-[#D4AF37]"
    }`;

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="feedback-float-btn"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Закрыть форму обратной связи" : "Написать нам"}
        aria-expanded={open}
        className={`fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          open ? "bg-white/10 backdrop-blur-md rotate-90" : "bg-[#D4AF37] hover:bg-[#F4D03F] hover:scale-110"
        }`}
        style={{ boxShadow: open ? "none" : "0 4px 24px rgba(212,175,55,0.4)" }}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-black" />}
      </button>

      {/* Panel */}
      {open && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 bg-black/50 z-[9998] lg:hidden" onClick={() => setOpen(false)} />

          <div
            data-testid="feedback-panel"
            className="fixed z-[9999] bg-[#0F0F0F] border border-white/10 shadow-2xl
              bottom-0 left-0 right-0 max-h-[85vh] rounded-t-2xl
              lg:bottom-24 lg:right-6 lg:left-auto lg:w-[360px] lg:rounded-xl lg:max-h-none"
            style={{ backdropFilter: "blur(16px)" }}
          >
            {/* Mobile drag handle */}
            <div className="flex justify-center pt-3 pb-1 lg:hidden">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            <div className="p-5 overflow-y-auto max-h-[80vh] lg:max-h-none">
              {success ? (
                <div data-testid="feedback-success" className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-green-400" />
                  </div>
                  <p className="text-lg font-medium text-white mb-1">Спасибо!</p>
                  <p className="text-sm text-[#A3A3A3]">Мы свяжемся с вами в ближайшее время</p>
                </div>
              ) : (
                <>
                  <h3 className="text-base font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Связаться с нами
                  </h3>

                  {/* Direct links */}
                  {(tg || wa) && (
                    <>
                      <div className="space-y-2 mb-4">
                        {tg && (
                          <a href={tg} target="_blank" rel="noopener noreferrer" data-testid="feedback-tg-btn"
                            className="flex items-center gap-3 w-full px-4 py-3 bg-[#229ED9]/10 border border-[#229ED9]/20 rounded-lg text-[#229ED9] hover:bg-[#229ED9]/20 transition-colors text-sm font-medium">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                            Написать в Telegram
                          </a>
                        )}
                        {wa && (
                          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="feedback-wa-btn"
                            className="flex items-center gap-3 w-full px-4 py-3 bg-[#25D366]/10 border border-[#25D366]/20 rounded-lg text-[#25D366] hover:bg-[#25D366]/20 transition-colors text-sm font-medium">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                            Написать в WhatsApp
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs text-[#555] uppercase tracking-widest">или</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>
                    </>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <input data-testid="feedback-name" value={form.name}
                        onChange={e => { setForm(p => ({ ...p, name: e.target.value })); if (!touched.name) setTouched(p => ({ ...p, name: true })); }}
                        className={inputCls(nameErr)} placeholder="Ваше имя" />
                      {nameErr && <p className="text-xs text-red-400 mt-1">{nameErr}</p>}
                    </div>
                    <div>
                      <input data-testid="feedback-phone" value={form.phone} type="tel"
                        onChange={e => handlePhoneChange(e.target.value)}
                        onFocus={() => { if (!form.phone) setForm(p => ({ ...p, phone: "+7 (" })); }}
                        className={inputCls(phoneErr)} placeholder="+7 (999) 123-45-67" />
                      {phoneErr && <p className="text-xs text-red-400 mt-1">{phoneErr}</p>}
                    </div>
                    <select data-testid="feedback-messenger" value={form.messenger}
                      onChange={e => setForm(p => ({ ...p, messenger: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-[#D4AF37]">
                      <option value="Telegram">Telegram</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="MAX">MAX</option>
                      <option value="Звонок">Звонок</option>
                    </select>
                    <textarea data-testid="feedback-message" value={form.message}
                      onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#555] outline-none focus:border-[#D4AF37] resize-none min-h-[70px]"
                      placeholder="Ваше сообщение..." />
                    <button data-testid="feedback-submit" type="submit" disabled={loading || !canSubmit}
                      className="w-full py-3 rounded-lg text-sm font-semibold uppercase tracking-widest transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed bg-[#D4AF37] text-black hover:bg-[#F4D03F]">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Отправить"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
